import { useEffect, useRef } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, drawSelection, dropCursor, highlightActiveLine, keymap, placeholder as placeholderExtension } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { languages } from '@codemirror/language-data';
import { tags } from '@lezer/highlight';
import { cn } from '../../lib/utils';
import { imageMarkdown, rejectImageFile, uploadPlaceholder } from './markdown-image';

type Props = {
  value: string;
  onChange: (markdown: string) => void;
  /** Uploads a pasted or dropped image and resolves to its URL. */
  upload: (file: File) => Promise<string>;
  /** Surfaces upload failures (unsupported file, network error). */
  onUploadError: (message: string) => void;
  placeholder?: string;
  className?: string;
};

/** Colours come from the app's CSS variables, so one style serves light and dark. */
const theme = EditorView.theme({
  '&': { backgroundColor: 'transparent', color: 'hsl(var(--foreground))', fontSize: '15px' },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': { fontFamily: 'inherit', lineHeight: '1.7' },
  '.cm-content': { padding: '0', caretColor: 'hsl(var(--foreground))' },
  '.cm-line': { padding: '0' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'hsl(var(--foreground))' },
  '.cm-activeLine': { backgroundColor: 'hsl(var(--muted) / 0.4)' },
  '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground': {
    backgroundColor: 'var(--editor-selection)',
  },
  '.cm-placeholder': { color: 'hsl(var(--muted-foreground))' },
});

const highlight = HighlightStyle.define([
  // A token takes only its most specific rule, so each heading level carries the weight itself.
  { tag: tags.heading, fontWeight: '700' },
  { tag: tags.heading1, fontWeight: '700', fontSize: '1.5em' },
  { tag: tags.heading2, fontWeight: '700', fontSize: '1.25em' },
  { tag: tags.heading3, fontWeight: '700', fontSize: '1.1em' },
  { tag: tags.emphasis, fontStyle: 'italic' },
  { tag: tags.strong, fontWeight: '700' },
  { tag: tags.strikethrough, textDecoration: 'line-through' },
  { tag: [tags.link, tags.url], color: 'hsl(var(--primary-text))', textDecoration: 'underline' },
  { tag: tags.monospace, backgroundColor: 'hsl(var(--muted))', borderRadius: '0.25rem' },
  { tag: tags.quote, color: 'hsl(var(--muted-foreground))' },
  { tag: [tags.processingInstruction, tags.labelName, tags.contentSeparator, tags.list], color: 'hsl(var(--muted-foreground))' },
  { tag: tags.atom, color: 'hsl(var(--primary-text))' },
  // Fenced code in the common languages.
  { tag: tags.keyword, color: 'hsl(var(--primary-text))' },
  { tag: [tags.string, tags.special(tags.string)], color: 'hsl(160 84% 30%)' },
  { tag: tags.comment, color: 'hsl(var(--muted-foreground))', fontStyle: 'italic' },
  { tag: [tags.number, tags.bool, tags.null], color: 'hsl(25 95% 45%)' },
]);

let uploadCounter = 0;

/**
 * Plain Markdown source editing (CodeMirror 6): what you type is exactly what is stored.
 * Images pasted or dropped into the text are uploaded and inserted as `![alt](url)`.
 */
export default function MarkdownSourceEditor({ value, onChange, upload, onUploadError, placeholder, className }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const uploadRef = useRef(upload);
  const onUploadErrorRef = useRef(onUploadError);
  onChangeRef.current = onChange;
  uploadRef.current = upload;
  onUploadErrorRef.current = onUploadError;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    function insertImages(view: EditorView, files: FileList, at: number): boolean {
      const images = Array.from(files);
      if (images.length === 0) return false;
      for (const file of images) {
        const rejection = rejectImageFile(file);
        if (rejection) {
          onUploadErrorRef.current(rejection);
          continue;
        }
        uploadCounter += 1;
        const placeholderText = uploadPlaceholder(`u${uploadCounter}`, file.name);
        view.dispatch({ changes: { from: at, insert: placeholderText } });
        uploadRef.current(file)
          .then((url) => replacePlaceholder(view, placeholderText, imageMarkdown(file.name, url)))
          .catch((err: unknown) => {
            replacePlaceholder(view, placeholderText, '');
            onUploadErrorRef.current(err instanceof Error && err.message ? err.message : 'Image upload failed.');
          });
      }
      return true;
    }

    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          drawSelection(),
          dropCursor(),
          highlightActiveLine(),
          EditorView.lineWrapping,
          keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
          markdown({ base: markdownLanguage, codeLanguages: languages }),
          syntaxHighlighting(highlight),
          theme,
          placeholderExtension(placeholder ?? ''),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) onChangeRef.current(update.state.doc.toString());
          }),
          EditorView.domEventHandlers({
            paste: (event, current) => {
              const files = event.clipboardData?.files;
              if (!files || files.length === 0) return false;
              event.preventDefault();
              return insertImages(current, files, current.state.selection.main.head);
            },
            drop: (event, current) => {
              const files = event.dataTransfer?.files;
              if (!files || files.length === 0) return false;
              event.preventDefault();
              const at = current.posAtCoords({ x: event.clientX, y: event.clientY }) ?? current.state.selection.main.head;
              return insertImages(current, files, at);
            },
          }),
        ],
      }),
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // The view is created once; `value` afterwards flows through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder]);

  // External resets (discard draft, restore a revision) replace the document; typing never re-enters here
  // because the parent echoes back exactly what the update listener emitted.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
  }, [value]);

  return <div ref={hostRef} className={cn('wiki-source font-mono', className)} />;
}

function replacePlaceholder(view: EditorView, placeholderText: string, replacement: string): void {
  const doc = view.state.doc.toString();
  const from = doc.indexOf(placeholderText);
  // The author deleted the placeholder while the upload ran; nothing to put the image after.
  if (from < 0) return;
  view.dispatch({ changes: { from, to: from + placeholderText.length, insert: replacement } });
}
